import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import configServ from "../../services/config";
import Swal from "sweetalert2"; // Make sure to install this if not already
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";

const MotionBox = motion(Box);

const RightDrawer = ({
  open,
  toggleDrawer,
  companyId,
  course,
  isEditt,
  setIsEditt,
  selectedCustomer,
setSelectedCustomer
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const drawerWidth = isMobile ? "80vw" : 400;

  const [customers, setCustomers] = useState([]);
  const [instructor, setInstructor] = useState([]);
  const [loadingInstructor, setLoadingInstructor] = useState(false);

  const [formData, setFormData] = useState({
    instructor_id: "",
    status: true,
  });
  const [validationErrors, setValidationErrors] = useState({});

  // 🔹 Fetch customers
  const fetchCustomerList = async () => {
    try {
      const result = await configServ.get_customer({
        company_id: companyId,
      });
      setCustomers(result);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchInstructor = async () => {
    try {
      setLoadingInstructor(true);
      const result = await configServ.getInstructorByCompanyAndCourse({
        company_id: companyId,
        course_id: course.id,
      });
      setInstructor(result?.data || []);
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingInstructor(false);
    }
  };

  // 🔹 Load customer list when drawer opens
  useEffect(() => {
    if (open) {
      fetchCustomerList();
      fetchInstructor();
    }
  }, [open]);

  // 🔹 Prefill data if editing
  // useEffect(() => {
  //   if (open && course && course.id) {
  //     setIsEditt(true);
  //     setFormData({
  //       instructor_id: course.instructor_id || "",
  //       status: course.status ?? true,
  //       id: course.id,
  //     });

  //     const matched = customers.find((c) => c.id === course.instructor_id);
  //     if (matched) setSelectedCustomer(matched);
  //   } else {
  //     setIsEditt(false);
  //     clear();
  //   }
  // }, [course, open, customers]);

  const validate = () => {
    const errors = {};
    if (!formData.instructor_id) {
      errors.instructor_id = "Please select a practitioner.";
    }
    return errors;
  };

  const clear = () => {
    setFormData({ instructor_id: "", status: true });
    setSelectedCustomer(null);
    setValidationErrors({});
    setIsEditt(false);
  };

  const highlightMatch = (text, query) => {
    try {
      const words = query.toLowerCase().split(" ").filter(Boolean);
      const regex = new RegExp(`(${words.join("|")})`, "gi");

      return text.split(regex).map((part, index) =>
        words.includes(part.toLowerCase()) ? (
          <mark key={index} style={{ backgroundColor: "#ffe58f" }}>
            {part}
          </mark>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        )
      );
    } catch (e) {
      return text;
    }
  };

  const handleSave = async () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      const payload = {
        ...formData,
        company_id: companyId,
        course_id: course.id,
      };
      console.log("payload", payload);
      if (isEditt) {
        const res = await configServ.updateinstructor(formData.id, payload);
        if (res.success) {
          Swal.fire({
            icon: "success",
            title: "Updated",
            text: "Instructor updated successfully.",
          });
          clear();
          toggleDrawer();
        }
      } else {
        const res = await configServ.storeinstructor(payload);
        if (res.success) {
          Swal.fire({
            icon: "success",
            title: "Created",
            text: "Instructor created successfully.",
          });
          clear();
          toggleDrawer();
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
    }
  };
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        const res = await configServ.deleteInstructor(id);
        if (res.success) {
          Swal.fire("Deleted!", "Instructor has been deleted.", "success");
          fetchInstructor();
        }
      } catch (e) {
        Swal.fire("Error", "Failed to delete instructor.", "error");
      }
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer}
      sx={{ zIndex: 10 }}
    >
      <MotionBox
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 100, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        sx={{
          width: drawerWidth,
          p: 3,
          height: "100%",
          boxSizing: "border-box",
          backgroundColor: "#fff",
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mt: { xs: 10, sm: 10, md: 0 }, // xs/sm: margin-top 2, md and above: 0
            }}
          >
            Course: {course?.title || "N/A"}
          </Typography>

          <IconButton onClick={toggleDrawer} color="error">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form Fields */}
        <Box mt={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                fullWidth
                size="small"
                id="instructor_id"
                options={customers}
                getOptionLabel={(option) => option.fname}
                filterOptions={(options, { inputValue }) => {
                  const words = inputValue
                    .toLowerCase()
                    .split(" ")
                    .filter(Boolean);

                  return options.filter((customer) =>
                    words.every(
                      (word) =>
                        customer.fname.toLowerCase().includes(word) ||
                        (customer.mobile && customer.mobile.includes(word))
                    )
                  );
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedCustomer(newValue);
                    setFormData({ ...formData, instructor_id: newValue.id });
                    setValidationErrors({
                      ...validationErrors,
                      instructor_id: "",
                    });
                  }
                }}
                value={selectedCustomer}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Firm Name"
                    fullWidth
                    error={!!validationErrors.instructor_id}
                    helperText={validationErrors.instructor_id}
                  />
                )}
                renderOption={(props, option, { inputValue }) => (
                  <li {...props}>
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">
                        {highlightMatch(option?.fname, inputValue)}
                      </Typography>
                      {option?.mobile && (
                        <Typography variant="caption" color="text.secondary">
                          {highlightMatch(option?.mobile, inputValue)}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />
            </Grid>

            {/* <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.checked })
                    }
                  />
                }
                label="Status Active"
              />
            </Grid> */}

            {/* Buttons */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <Button variant="outlined" color="secondary" onClick={clear}>
                  Reset
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={!formData.instructor_id}
                >
                  {isEditt ? "Update" : "Save"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        {/* Instructor List */}
        <Box mt={4}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Assigned Instructors
          </Typography>

          {loadingInstructor ? (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
            </Box>
          ) : instructor.length === 0 ? (
            <Typography color="text.secondary">
              No instructors assigned yet.
            </Typography>
          ) : (
            instructor.map((ins) => (
              <Box
                key={ins.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                border="1px solid #ddd"
                borderRadius={2}
                p={2}
                mb={1}
              >
                <Typography>{ins.instructor_name}</Typography>
                <Box>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setIsEditt(true);
                      setFormData({
                        instructor_id: ins.instructor_id,
                        id: ins.id,
                        status: ins.status,
                      });
                      const matched = customers.find(
                        (c) => c.id === ins.instructor_id
                      );
                      if (matched) setSelectedCustomer(matched);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(ins.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </MotionBox>
    </Drawer>
  );
};

export default RightDrawer;
